import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useContent } from '../context/ContentContext';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  width: 100%;
  background: var(--dark-bg);
  padding: 1rem 0;
  z-index: 1000;
  transition: var(--transition-smooth);
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);

  &.scrolled {
    padding: 0.5rem 0;
    box-shadow: var(--shadow-md);
    background: rgba(44, 44, 44, 0.98);
    backdrop-filter: blur(10px);
  }
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  text-decoration: none;
  color: white;
`;

const LogoBox = styled.div`
  background: white;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid var(--primary-color);
  transition: var(--transition-smooth);

  ${Logo}:hover & {
    transform: rotate(-5deg) scale(1.05);
    box-shadow: var(--shadow-primary);
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.2;
`;

const LogoMain = styled.span`
  font-weight: 700;
  font-size: 1.2rem;
  letter-spacing: 1px;
`;

const LogoSub = styled.span`
  font-size: 0.8rem;
  font-weight: 400;
`;

const NavMenu = styled.ul`
  display: flex;
  list-style: none;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    position: fixed;
    top: 70px;
    left: ${props => props.$isOpen ? '0' : '-100%'};
    width: 100%;
    height: calc(100vh - 70px);
    background: rgba(44, 44, 44, 0.98);
    backdrop-filter: blur(10px);
    flex-direction: column;
    padding: 2rem;
    transition: left 0.3s ease-in-out;
    align-items: flex-start;
    gap: 1.5rem;
    overflow-y: auto;
    box-shadow: ${props => props.$isOpen ? '2px 0 10px rgba(0,0,0,0.3)' : 'none'};
    z-index: 999;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: white;
  transition: var(--transition-smooth);
  position: relative;
  padding: 0.5rem 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: var(--primary-color);
    transition: var(--transition-smooth);
  }

  &:hover::after {
    width: 100%;
  }

  &:hover {
    color: var(--primary-color);
  }
`;

const CtaNav = styled(Link)`
  background: var(--primary-color);
  padding: 0.7rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  transition: var(--transition-smooth);
  box-shadow: var(--shadow-sm);
  text-decoration: none;
  color: white;

  &::after {
    display: none;
  }

  &:hover {
    background: var(--primary-hover);
    color: white !important;
    transform: translateY(-2px);
    box-shadow: var(--shadow-primary);
  }
`;

const MobileToggle = styled.button`
  display: none;
  flex-direction: column;
  background: none;
  border: none;
  cursor: pointer;
  gap: 5px;
  padding: 10px;
  z-index: 1001;

  span {
    width: 30px;
    height: 3px;
    background: white;
    transition: all 0.3s ease;
    transform-origin: center;
  }

  ${props => props.$isOpen && `
    span:nth-child(1) {
      transform: rotate(45deg) translateY(8px);
    }
    span:nth-child(2) {
      opacity: 0;
    }
    span:nth-child(3) {
      transform: rotate(-45deg) translateY(-8px);
    }
  `}

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Navigation = () => {
  const siteData = useContent();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleHashLink = (e, path) => {
    if (path.includes('#')) {
      e.preventDefault();
      const [pathname, hash] = path.split('#');
      
      if (location.pathname !== pathname) {
        window.location.href = path;
      } else {
        const element = document.getElementById(hash);
        if (element) {
          const offsetTop = element.offsetTop - 80;
          window.scrollTo({
            top: offsetTop,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  return (
    <Nav className={isScrolled ? 'scrolled' : ''}>
      <NavContainer>
        <Logo to="/">
          <LogoBox>
            <LogoImage src="/hasnat.png" alt={`${siteData.company.name} logo`} />
          </LogoBox>
          <LogoText>
            <LogoMain>{siteData.company.name}</LogoMain>
            <LogoSub>{siteData.company.tagline}</LogoSub>
          </LogoText>
        </Logo>
        <NavMenu $isOpen={isMobileMenuOpen}>
          {siteData.navigation.map((item, index) => (
            <li key={index}>
              <NavLink 
                to={item.path}
                onClick={(e) => {
                  handleHashLink(e, item.path);
                  setIsMobileMenuOpen(false);
                }}
              >
                {item.name}
              </NavLink>
            </li>
          ))}
          <li>
            <CtaNav to="/contact" onClick={() => setIsMobileMenuOpen(false)}>Get Quote!</CtaNav>
          </li>
        </NavMenu>
        <MobileToggle 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          $isOpen={isMobileMenuOpen}
          aria-label="Toggle menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </MobileToggle>
      </NavContainer>
    </Nav>
  );
};

export default Navigation;
